/*
 * Copyright (c) 2018 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {areTypesEqual} from '@ion/core/src/entities/GroupEntityTypes';
import {error, info} from '@ion/core/src/logger/logger';
import {
  Account,
  GenericObject,
  GroupCategoryEntity,
  GroupTypeEntity,
  InheritableProperty,
  InheritablePropertyValue,
  Link
} from '@ion/core/src/types/entities';
import {get} from '../environment/environment';
import {getWebMapIdFromName} from './ArcGIS';
import {getInheritedProperties} from './MicroServices';

export type Results = (
  InheritableProperty | InheritablePropertyValue
);

export type GroupPropertiesCache = {
  [id: number]: GenericObject;
};

/**
 * The Group Properties API encapsulates the management of inherited Group specific
 * properties, including retrieval, caching, and access of user defined entity types,
 * entity categories, customer account specific webMapIds, and overspeedThresholds.
 *
 * While clients can use the GroupProperties singleton directly, generally one need
 * only invoke the getInheritedProperties method when the current group changes, and
 * can subsequently use the exported convenience functions such as getAssetTypes,
 * getAssetCategories, etc..
 *
 * ```typescript
 *
 *   import GroupProperties from '@ion/core/src/services/GroupProperties';
 *
 *   // load all group specific properties for the given group id ...
 *   const properties = await GroupProperties.getInheritedProperties(groupId);
 *
 *   // retrieve the types for the group properties ...
 *   const assetTypes         = getAssetTypes();
 *   const operatorTypes      = getOperatorTypes();
 *
 *   // retrieve the categories for the group properties ...
 *   const assetCategories    = getAssetCategories();
 *   const operatorCategories = getOperatorCategories();
 *   const routeCategories    = getRouteCategories();
 *   const landmarkCategories = getLandmarkCategories();
 *
 *   // retrieve the webMapId and overspeedThreshold for the group properties ...
 *   const webMapId           = getWebMapId();
 *   const overspeedThreshold = getOverspeedThreshold();
 *
 * ```
 */
class GroupProperties {
  /**
   * @private
   */
  _properties: GenericObject;

  /**
   * @private
   *
   * Provides the cache of group ids to their inherited properties returned
   * by AO MicroServices. This is a pseudo-private static member and should
   * not be accessed directly.
   */
  _cache: GroupPropertiesCache = {};

  /**
   * @private
   *
   * By default, the GroupProperties API caches the properties retrieved for
   * each group id. To disable Group Properties caching, the _reset
   */
  _cacheEnabled: boolean = true;

  /**
   * @private
   *
   * If caching is enabled, provides a reference to the id of the Group whose
   * inherited properties are currently available. This is a pseudo-private
   * static member and should not be accessed directly.
   */
  _currentGroupId: number | null = null;

  /**
   * Retrives the Inheritable Group Properties for the given Group Id from
   * AO MicroServices. If caching is enabled, and the Group Properties have
   * already been retrieved for the given Group Id, the returned Promise will
   * be resolved immediately, returning the cached properties for the Group.
   *
   * ```typescript
   *
   *   import GroupProperties from '@ion/core/src/services/GroupProperties';
   *
   *   const properties = await GroupProperties.getInheritedProperties(groupId);
   *
   * ```
   *
   * @method  getInheritedProperties
   * @param   {number} id The id of the Group for which Inherited Properties
   *          are to be returned.
   * @returns {Promise} A Promise which resolves once the Inherited Group
   *          Properties have been retrieved from AO MicroServices. If caching
   *          is enabled, and the Group Properties have already been retrieved,
   *          the Promise will resolve immediately with the cached properties.
   *
   */
  async getInheritedProperties(id: number, update = false): Promise<GenericObject|boolean> {
    const cached = this._fromCache(id);
    if ((!cached && id) || update) {
      try {
        const response     = await getInheritedProperties(id);
        const {webMapName} = response;
        const webMapId     = Object.assign({}, webMapName, {value: get('webMapId')});
        if (webMapName?.value) {
          const webMaps = await getWebMapIdFromName(webMapName.value);
          if (webMaps?.results) {
            const webMap = webMaps.results.find(({title}) => title === webMapName.value);
            if (webMap) {
              webMapId.value = webMap.id;
            }
          }
        }
        this._properties = this._fromCache(id, Object.assign(response, {webMapId}));
        return this._properties;
      } catch (err) {
        error(`Group Properties: Property retrieval for group ${id} failed.`, err);
        return false;
      }
    }
    info(`Group Properties: Retrieved from Cache, Current Group ${id}`);
    this._properties = cached;
    return Promise.resolve(cached);
  }

  /**
   * Allows for setting the current inherited properties group scope. By default,
   * a component's Group Selector specifies the scope of the group properties API.
   * When the component is unmounted, the scope should be reset back to the current
   * global group, the user's default group, or null.
   *
   * ```typescript
   *
   *   import GroupProperties from '@ion/core/src/services/GroupProperties';
   *
   *   // set the currently scoped group id ...
   *   GroupProperties.setGroupId(groupId);
   *
   * ```
   *
   * @method  setGroupId
   * @param   {number} id The id of the group which is to be used to set the
   *          scope of the inherited group properties, or null of no group
   *          scope is to be set.
   */
  setGroupId(id: number): void {
    this._properties = this._fromCache(id);
    this._currentGroupId = id;
  }

  /**
   * Returns the value of the currently scoped Group id from which all group
   * properties will be provided.
   *
   * @method  getGroupId
   * @returns {number} id The id of the Group whose Inherited Properties
   *          are currently available.
   */
  getGroupId(): number | null {
    return this._currentGroupId;
  }

  /**
   * Returns the the provided type properties for the given GroupTypeEntity.
   *
   * ```typescript
   *
   *   import GroupProperties from '@ion/core/src/services/GroupProperties';
   *
   *   const types = GroupProperties.getEntityTypes(GroupTypeEntity.asset, true, false);
   *
   * ```
   *
   * @method getEntityTypes
   * @see getAssetTypes
   * @see getOperatorTypes
   * @see getProperty
   */
  getEntityTypes(entity: GroupTypeEntity, valueOnly = false, provide = true): Results {
    return this.getProperty(`${entity}Types`, valueOnly, provide);
  };

  /**
   * Returns the the provided category properties for the given GroupTypeEntity.
   *
   * ```typescript
   *
   *   import GroupProperties from '@ion/core/src/services/GroupProperties';
   *
   *   const categories = GroupProperties.getEntityCategories(GroupTypeEntity.asset, true);
   *
   * ```
   *
   * @method getEntityCategories
   * @see getAssetTypes
   * @see getOperatorTypes
   * @see getProperty
   */
  getEntityCategories(entity: GroupCategoryEntity, valueOnly = false, provide = true): Results {
    return this.getProperty(`${entity}Categories`, valueOnly, provide);
  };

  /**
   * Provides a utility for determining if the given type exists within
   * the list of types for the type's entity, either 'asset' or 'operator'.
   *
   * @see     getProperty
   * @method  hasType
   * @param   {string} entity The type entity; 'asset' or 'operator'
   * @param   {string} type The type to be found.
   * @returns {boolean} true if the type exists within the current types
   *          for the given type entity.
   */
  hasType(entity: GroupTypeEntity, type): boolean {
    const types: string[] = this.getProperty(`${entity}Types`, true, true);
    if (types?.length) {
      return !isNil(types.find((val: string) => areTypesEqual(entity, val, type)));
    }
    return false;
  }

  /**
   * Provides a utility for determining if the given category exists within
   * the list of categories for the category type, either 'asset', 'operator',
   * or 'route'.
   *
   * @see     getProperty
   * @method  hasCategory
   * @param   {string} type The category type; 'asset', 'operator', 'route'
   * @param   {string} category The category to be found.
   * @returns {boolean} true if the category is exists within the current
   *          categories for the given category type.
   */
  hasCategory = (type: GroupCategoryEntity, category: InheritablePropertyValue): boolean => {
    const categories: string[] = this.getProperty(`${type}Categories`, true, true);
    if (categories?.length) {
      return !isNil(categories.find((val: string) => val === category));
    }
    return false;
  }

  /**
   * Given an inheritable property name, returns the property from the provided
   * properties.
   *
   * Group property definitions are modeled as an object which contains a value
   * attribute, and a HATEOAS account link. This method can be called to retrieve
   * the complete property definition, or just the value. To only have the value
   * returned, the valueOnly argument can be set to true. In addition, for client
   * code which depends on both the value and account, the provide argument can
   * be set to true to ensure the definition is always returned, even when null.
   *
   * ```typescript
   *
   *   import GroupProperties from '@ion/core/src/services/GroupProperties';
   *
   *   // retrieve the 'webMapId' property definition ...
   *   GroupProperties.getProperty('webMapId');
   *   // {
   *   //    value: '81f1e723a81b4e5293c769516857049a',
   *   //    account:{
   *   //      title: 'CACTC',
   *   //      href: https://connect.calamp.com/connect/services/accounts/123
   *   //     }
   *   //  }
   *
   *   // retrieve the 'webMapId' property definitions value only ...
   *   GroupProperties.getProperty('webMapId', true);
   *   // '81f1e723a81b4e5293c769516857049a'
   *
   *
   *   // retrieve the 'webMapId' property definition, regardless if null ...
   *   GroupProperties.getProperty('webMapId', true, true);
   *   // {
   *   //    value: null,
   *   //    account: {
   *   //      title: null,
   *   //      href: null
   *   //  }
   *
   * ```
   *
   * @private
   * @method getProperty
   * @param  {string} name The name of the inheritied property to be returned.
   * @param  {boolean} valueOnly When true, indicates that only the value
   *         of the inherited property is to be returned, otherwise, when
   *         false (default), the object containing the inhertied property
   *         and the source of the property is returned.
   * @param  {boolean} provide When true, indicates that the data structure
   *         should be returned regardless if the property has any values.
   *         This simplifies the need to check for nulls within the result,
   *         value, and account properties.
   */
  getProperty(name: string, valueOnly = false, provide = false): any {
    if (this._properties) {
      const property = this._properties[name];
      if (property) {
        return valueOnly ? property.value : property;
      }
    }
    if (provide) {
      return this.toDefinition();
    }
  };

  /**
   * Provides a utility which returns an empty Group Property defintion based
   * on the given account (if provided). This simplifies the need to check for
   * nulls when referencing Group properties which may be entirely null, or
   * have a null account or account properties.
   *
   * ```typescript
   *
   *   import GroupProperties from '@ion/core/src/services/GroupProperties';
   *
   *   // retrieve the 'webMapId' property definition, regardless if null ...
   *   const definition = GroupProperties.toDefinition('webMapId');
   *   // {
   *   //    value: null,
   *   //    account: {
   *   //      title: null,
   *   //      href: null
   *   //  }
   *
   * ```
   *
   * @method  toDefinition
   * @param   {InheritablePropertyValue} value The default value to be used as the value property.
   * @param   {Account} account The default account to use as the account property.
   * @returns {InheritableProperty} A group property definition object containing a null
   *          value and empty account (if the account argument is not provided).
   */
  toDefinition(value?: InheritablePropertyValue, account?: Link | Account): InheritableProperty {
    const definition: {value: any, account?: Link} = {value};
    if (account) {
      const {name, _self} = account as Account;
      const {title, href} = account as Link;
      definition.account = {
        title  : name || title,
        href   : _self ? _self.href : href,
        status : status || _self ? _self.status : account.status
      };
    } else {
      definition.account = {title: null, href: null, status: null};
    }
    return definition;
  }

  /**
   * Allows for determining if caching is currently enabled for the Group
   * Properties API.
   *
   * ```typescript
   *
   *   import GroupProperties from '@ion/core/src/services/GroupProperties';
   *
   *   const isCacheEnabled = GroupProperties.isCacheEnabled(); // true | false
   *
   * ```
   *
   * @method  isCacheEnabled
   * @returns {boolean} True if caching is enabled, otherwise, false.
   */
  isCacheEnabled(): boolean {
    return this._cacheEnabled;
  }

  /**
   * Allows for enabling or disabling caching of retrievied Group properties.
   * By default, caching is enabled, which results in faster Group property
   * retrieval, but also results in static returns of Group Properties, meaning,
   * the properties are only retrieved once. If Group Properties need to be
   * retrieved from MicroServices with each request, caching can be disabled
   * to ensure the latest properties are always returned.
   *
   * ```typescript
   *
   *   import GroupProperties from '@ion/core/src/services/GroupProperties';
   *
   *   const enabled = GroupProperties.enableCache(true); // true | false
   *
   * ```
   *
   * @method enableCache
   * @param  {boolean} enable When true (default), results in caching being
   *         enabled. Otherwise, if false, caching will be disabled and the
   *         internal cache will be nullified.
   */
  enableCache(enable = true): boolean {
    this._cache = enable ? {} : null;
    info(`Group Properties: Cache Enabled - ${enable}`);
    return (this._cacheEnabled = enable);
  }

  /**
   * Allows for removing the group properties for a previously cached group.
   *
   * @method removeFromCache
   * @param  {number} id The id of the Group for which cached properties are
   *         to be removed.
   */
  removeFromCache = (id: number): boolean => {
    if (this._cacheEnabled && this._cache) {
      if (this._currentGroupId === id) {
        this._currentGroupId = null;
      }
      delete this._cache[id];
      return true;
    }
    return false;
  }

  /**
   * Allows for resetting the internal Group Properties cache, which effectively
   * results in request for Group properties which previously have been cached
   * being retrieved from MicroServices, and re-persisted within the cache.
   *
   * @method resetCache
   */
  resetCache(): boolean {
    if (this._cacheEnabled) {
      this._currentGroupId = null;
      this._cache = {};
      return true;
    }
    return false;
  }

  /**
   * The isCacheExist method is a utility function used to check if a specific
   * item with a given id exists within a cache. This method is particularly
   * useful in scenarios where you want to verify whether a cached item is present
   * before attempting to retrieve or manipulate it.
   *
   * ```typescript
   *
   *  import GroupProperties from '@ion/core/src/services/GroupProperties';
   *
   *  const isExist = GroupProperties.isCacheExist(5099); // true | false
   *
   * ```
   *
   * @param id The unique identifier of the item you want to check for in the cache.
   * @returns boolean: Returns true if an item with the specified id exists in the cache;
   * otherwise, it returns false.
   */
  isCacheExist(id: number): boolean {
    return !!this._cache?.[id];
  }

  /**
   * @private
   *
   * Allows for retrieving Group Properties directly from the properties
   * cache when caching is enabled. This method is primarily used internally.
   *
   * @method  fromCache
   * @param   {number} id The id of the Group for which Inherited Properties
   *          are to be returned.
   * @returns {object} The cached Group Properties for the given id, otherwise,
   *          undefined.
   */
  _fromCache(id: number, properties?: GenericObject): GenericObject {
    if (this._cacheEnabled) {
      if (properties) {
        this._cache[id] = properties;
      }
      this._currentGroupId = id;
      return this._cache?.[id];
    }
    return properties;
  }
}

/**
 * @function getAssetTypes
 *
 * Returns the inherited Asset Types for the provided properties.
 *
 * ```typescript
 *
 *   import {getAssetTypes} from '@ion/core/src/services/GroupProperties';
 *
 *   const types: string[] = getAssetTypes();
 *
 * ```
 */
export const getAssetTypes = (valueOnly = true, provide = false): Results => {
  return api.getEntityTypes(GroupTypeEntity.asset, valueOnly, provide);
};

/**
 * @function getOperatorTypes
 *
 * Returns the inherited Operator Types for the provided properties.
 *
 * ```typescript
 *
 *   import {getOperatorTypes} from '@ion/core/src/services/GroupProperties';
 *
 *   const types: string[] = getOperatorTypes();
 *
 * ```
 *
 */
export const getOperatorTypes = (valueOnly = true, provide = false): Results => {
  return api.getEntityTypes(GroupTypeEntity.operator, valueOnly, provide);
};

/**
 * @function getAssetCategories
 *
 * Returns the inherited Asset Categories for the provided properties.
 *
 * ```typescript
 *
 *   import {getAssetCategories} from '@ion/core/src/services/GroupProperties';
 *
 *   const categories: string[] = getAssetCategories();
 *
 * ```
 *
 */
export const getAssetCategories = (valueOnly = true, provide = false): Results => {
  return api.getEntityCategories(GroupCategoryEntity.asset, valueOnly, provide);
};

/**
 * @function getOperatorCategories
 *
 * Returns the inherited Operator Categories for the provided properties.
 *
 * ```typescript
 *
 *   import {getOperatorCategories} from '@ion/core/src/services/GroupProperties';
 *
 *   const categories: string[] = getOperatorCategories();
 *
 * ```
 *
 */
export const getOperatorCategories = (valueOnly = true, provide = false): Results => {
  return api.getEntityCategories(GroupCategoryEntity.operator, valueOnly, provide);
};

/**
 * @function getRouteCategories
 *
 * Returns the inherited Route Categories for the provided properties.
 *
 * ```typescript
 *
 *   import {getRouteCategories} from '@ion/core/src/services/GroupProperties';
 *
 *   const categories: string[] = getRouteCategories();
 *
 * ```
 *
 */
export const getRouteCategories = (valueOnly = true, provide = false): Results => {
  return api.getEntityCategories(GroupCategoryEntity.route, valueOnly, provide);
};

/**
 * @function getLandmarkCategories
 *
 * Returns the inherited Landmark / GeoFence Categories for the provided
 * properties.
 *
 * ```typescript
 *
 *   import {getLandmarkCategories} from '@ion/core/src/services/GroupProperties';
 *
 *   const categories: string[] = getLandmarkCategories();
 *
 * ```
 *
 */
export const getLandmarkCategories = (valueOnly = true, provide = false): Results => {
  return api.getEntityCategories(GroupCategoryEntity.landmark, valueOnly, provide);
};

/**
 * @function getWebMapId
 *
 * Returns the inherited webMapId for the current inherited Group properties.
 *
 * ```typescript
 *
 *   import {getWebMapId} from '@ion/core/src/services/GroupProperties';
 *
 *   const webMapId: string = getWebMapId();
 *
 * ```
 *
 */
export const getWebMapId = (valueOnly = true, provide = false): Results => {
  return api.getProperty('webMapId', valueOnly, provide);
};

/**
 * @function getOverspeedThreshold
 *
 * Returns the inherited Overspeed Threshold for the provided properties.
 *
 * ```typescript
 *
 *   import {getOverspeedThreshold} from '@ion/core/src/services/GroupProperties';
 *
 *   const overspeedThreshold: number = getOverspeedThreshold();
 *
 * ```
 *
 */
export const getOverspeedThreshold = (valueOnly = true, provide = false): Results => {
  return api.getProperty('overspeedThreshold', valueOnly, provide);
};

/**
 * @function getUrgentNotificationsEnabled
 *
 * Returns the Urgent Notifications Enabled preference value.
 *
 * ```typescript
 *
 *    import {getUrgentNotificationsEnabled} from '@ion/core/src/services/GroupProperties';
 *
 *    const urgentNotificationsEnabled: boolean = getUrgentNotificationsEnabled();
 *
 * ```
 */
export const getUrgentNotificationsEnabled = (valueOnly = true, provide = false): boolean => {
  return api.getProperty('urgentNotificationsEnabled', valueOnly, provide) ?? false;
};

/**
 * Returns inherited dateFormat for the current inherited Group properties.
 *
 * ```typescript
 *
 *   import {getDateFormat} from '@ion/core/src/services/GroupProperties';
 *
 *   const dateFormat: string = getDateFormat();
 *
 * ```
 */
export const getDateFormat = (valueOnly = true, provide = false): string => {
  return api.getProperty('dateFormat', valueOnly, provide);
};

/**
 * @function getNotificationsAlertSoundEnabled
 *
 * Returns the Notifications Alert Sound Enabled preference value.
 *
 * ```typescript
 *
 *    import {getNotificationsAlertSoundEnabled} from '@ion/core/src/services/GroupProperties';
 *
 *    const notificationsAlertSoundEnabled: boolean = getNotificationsAlertSoundEnabled();
 *
 * ```
 */
export const getNotificationsAlertSoundEnabled = (valueOnly = true, provide = false): boolean => {
  return api.getProperty('notificationsAlertSoundEnabled', valueOnly, provide) ?? false;
};

/**
 * Returns the inherited timeFormat for the current inherited Group properties.
 *
 * ```typescript
 *
 *   import {getTimeFormat} from '@ion/core/src/services/GroupProperties';
 *
 *   const timeFormat: string = getTimeFormat();
 *
 * ```
 *
 */
export const getTimeFormat = (valueOnly = true, provide = false) => {
  return api.getProperty('timeFormat', valueOnly, provide);
};

/**
 * Returns the inherited timeZone for the current inherited Group properties.
 *
 * ```typescript
 *
 *   import {getTimeZone} from '@ion/core/src/services/GroupProperties';
 *
 *   const timeZone: string = getTimeZone();
 *
 * ```
 *
 */
export const getTimeZone = (valueOnly = true, provide = false) => {
  return api.getProperty('timeZone', valueOnly, provide);
};

/**
 * Returns the inherited driver scores for the current inherited Group properties.
 *
 * ```typescript
 *
 *   import {getDriverScores} from '@ion/core/src/services/GroupProperties';
 *
 *   const score = getDriverScores();
 *
 * ```
 *
 */
export const getDriverScores = (valueOnly = true, provide = false) => {
  return api.getProperty('driverScoring', valueOnly, provide);
};

/**
 * Returns the inherited operator ID type for the current inherited Group properties.
 *
 * ```typescript
 *
 *   import {getOperatorIdType} from '@ion/core/src/services/GroupProperties';
 *
 *   const operatorIdType = getOperatorIdType();
 *
 * ```
 *
 */
export const getOperatorIdType = (valueOnly = true, provide = false) => {
  return api.getProperty('operatorIdType', valueOnly, provide);
};

const api = new GroupProperties();
export default api;
